import { useEffect, useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { toast } from "sonner";
import { createPost, listPosts, deletePost, updatePost, fetchPostsStats } from "../lib/api";
import { ARTISTS, artistName } from "../lib/artists";
import { X, Upload, Trash2, Plus, Pencil } from "lucide-react";

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

const initial = { caption: "", artist: "jay", tags: "" };

const formatDate = (d) => {
  try {
    return new Date(d).toLocaleString("en-AU", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return d;
  }
};

export default function AdminPosts() {
  const [posts, setPosts] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_artist: [] });
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(initial);
  const [files, setFiles] = useState([]); // {name, data_url, preview}
  const [submitting, setSubmitting] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const startEdit = (p) => {
    setEditing(p.id);
    setEditForm({
      caption: p.caption || "",
      artist: p.artist || "jay",
      tags: (p.tags || []).join(", "),
      alts: p.images.map((im) => im.alt || ""),
    });
  };

  const cancelEdit = () => { setEditing(null); setEditForm(null); };

  const saveEdit = async (id) => {
    setSaving(true);
    try {
      await updatePost(id, {
        caption: editForm.caption,
        artist: editForm.artist,
        tags: editForm.tags
          .split(",")
          .map((t) => t.trim().replace(/^#/, "").toLowerCase())
          .filter(Boolean),
        image_alts: editForm.alts.map((a) => (a || "").trim()),
      });
      toast.success("Post updated");
      cancelEdit();
      refresh();
    } catch {
      toast.error("Failed to update");
    } finally {
      setSaving(false);
    }
  };

  const refresh = async () => {
    setLoading(true);
    try {
      const [p, s] = await Promise.all([listPosts({ limit: 100 }), fetchPostsStats()]);
      setPosts(p);
      setStats(s);
    } catch {
      toast.error("Failed to load posts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const onDrop = useCallback(async (accepted) => {
    const next = [];
    for (const f of accepted.slice(0, 10 - files.length)) {
      if (f.size > 8 * 1024 * 1024) {
        toast.error(`${f.name} is larger than 8MB`);
        continue;
      }
      const data_url = await fileToDataUrl(f);
      next.push({ name: f.name, data_url, preview: data_url, alt: "" });
    }
    setFiles((prev) => [...prev, ...next].slice(0, 10));
  }, [files.length]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [] },
    maxFiles: 10,
  });

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (files.length === 0) {
      toast.error("Add at least one image");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        caption: form.caption,
        artist: form.artist,
        tags: form.tags
          .split(",")
          .map((t) => t.trim().replace(/^#/, "").toLowerCase())
          .filter(Boolean),
        images: files.map(({ name, data_url, alt }) => ({ name, data_url, alt: (alt || "").trim() })),
      };
      await createPost(payload);
      toast.success("Post published");
      setForm(initial);
      setFiles([]);
      refresh();
    } catch (err) {
      toast.error("Failed to publish");
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await deletePost(id);
      toast.success("Deleted");
      refresh();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div data-testid="admin-posts" className="space-y-10">
      {/* Composer */}
      <section className="bg-white border border-[#E5E5E5] p-6 md:p-8">
        <div className="flex items-center gap-3 mb-6">
          <Plus size={16} />
          <h2 className="overline">New Post</h2>
        </div>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-2">
          <div className="md:col-span-2">
            <label className="overline block mt-2 mb-3">Description</label>
            <textarea
              data-testid="post-description"
              value={form.caption}
              onChange={update("caption")}
              rows={3}
              placeholder="Fresh piece · today's session…"
              className="editorial-input resize-none"
            />
          </div>
          <div>
            <label className="overline block mt-2 mb-3">Artist</label>
            <select
              data-testid="post-artist"
              value={form.artist}
              onChange={update("artist")}
              className="editorial-input cursor-pointer"
            >
              {ARTISTS.filter((a) => a.role !== "Management").map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
            <label className="overline block mt-8 mb-3">Tags <span className="normal-case tracking-normal text-[#999]">(comma separated)</span></label>
            <input
              data-testid="post-tags"
              value={form.tags}
              onChange={update("tags")}
              placeholder="fineline, forearm, blackandgrey"
              className="editorial-input"
            />
          </div>

          <div className="md:col-span-3 mt-6">
            <div className="overline mb-3">Images <span className="normal-case tracking-normal text-[#999]">(up to 10 · max 8MB each)</span></div>
            <div
              {...getRootProps()}
              data-testid="post-dropzone"
              className={`border border-dashed py-8 px-6 text-center cursor-pointer transition-colors ${
                isDragActive ? "border-[#0A0A0A] bg-[#FAFAFA]" : "border-[#CCCCCC] hover:border-[#0A0A0A]"
              }`}
            >
              <input {...getInputProps()} data-testid="post-file-input" />
              <Upload size={18} className="mx-auto mb-2 text-[#666]" />
              <p className="text-sm text-[#666]">
                {isDragActive ? "Drop the images…" : "Drag images here, or click to select"}
              </p>
            </div>
            {files.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                {files.map((f, i) => (
                  <div key={i}>
                    <div className="relative aspect-square bg-[#F5F5F5] overflow-hidden">
                      <img src={f.preview} alt={f.name} className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => setFiles((p) => p.filter((_, idx) => idx !== i))}
                        className="absolute top-1 right-1 bg-white/90 p-1 hover:bg-white"
                      >
                        <X size={12} />
                      </button>
                    </div>
                    <label className="overline block mt-2 mb-1 text-[9px]">Alt Text</label>
                    <input
                      data-testid={`post-image-alt-${i}`}
                      value={f.alt}
                      onChange={(e) =>
                        setFiles((p) => p.map((x, idx) => (idx === i ? { ...x, alt: e.target.value } : x)))
                      }
                      placeholder="e.g. Fine line rose tattoo on the forearm"
                      className="editorial-input text-sm"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="md:col-span-3 flex justify-end mt-6">
            <button
              type="submit"
              disabled={submitting}
              data-testid="post-submit"
              className="inline-flex items-center gap-3 bg-[#0A0A0A] text-white px-8 py-3 text-xs tracking-[0.28em] uppercase font-medium hover:bg-[#333] transition-colors disabled:opacity-60"
            >
              {submitting ? "Publishing…" : "Publish"} <span>→</span>
            </button>
          </div>
        </form>
      </section>

      {/* Stats */}
      <div className="flex flex-wrap items-center gap-3">
        <span className="overline">Journal</span>
        <span className="text-sm">{stats.total} posts</span>
        {stats.by_artist?.slice(0, 6).map((row) => (
          <span key={row.artist} className="text-[10px] tracking-[0.2em] uppercase text-[#666]">
            · {artistName(row.artist)} ({row.count})
          </span>
        ))}
      </div>

      {/* List */}
      <section className="bg-white border border-[#E5E5E5]">
        {loading ? (
          <div className="p-10 text-center text-[#666] text-sm tracking-widest uppercase">Loading…</div>
        ) : posts.length === 0 ? (
          <div className="p-10 text-center text-[#666] text-sm tracking-widest uppercase" data-testid="posts-empty">No posts yet.</div>
        ) : (
          <div className="divide-y divide-[#EFEFEF]">
            {posts.map((p) => (
              <div key={p.id} data-testid={`post-row-${p.id}`} className="p-4">
                <div className="grid grid-cols-12 gap-4 items-center">
                  <div className="col-span-3 md:col-span-2">
                    <div className="aspect-square bg-[#F5F5F5] overflow-hidden">
                      <img src={p.images[0]?.data_url} alt="" className="w-full h-full object-cover" style={{ filter: "grayscale(100%)" }} />
                    </div>
                  </div>
                  <div className="col-span-6 md:col-span-8">
                    <div className="overline mb-1">{artistName(p.artist)} · {formatDate(p.created_at)}</div>
                    <p className="text-sm text-[#333] leading-relaxed line-clamp-2">{p.caption || <span className="italic text-[#999]">No description</span>}</p>
                    {p.tags?.length > 0 && (
                      <div className="mt-1 text-[10px] text-[#888]">
                        {p.tags.map((t) => `#${t}`).join(" ")}
                      </div>
                    )}
                    <div className="text-[10px] text-[#999] mt-1">{p.images.length} image{p.images.length > 1 ? "s" : ""}</div>
                  </div>
                  <div className="col-span-3 md:col-span-2 flex flex-col items-end gap-3">
                    <button
                      onClick={() => (editing === p.id ? cancelEdit() : startEdit(p))}
                      data-testid={`post-edit-${p.id}`}
                      className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#0A0A0A] hover:opacity-70"
                    >
                      <Pencil size={14} /> {editing === p.id ? "Close" : "Edit"}
                    </button>
                    <button
                      onClick={() => onDelete(p.id)}
                      data-testid={`post-delete-${p.id}`}
                      className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-red-700 hover:opacity-70"
                    >
                      <Trash2 size={14} /> Delete
                    </button>
                  </div>
                </div>

                {editing === p.id && editForm && (
                  <div
                    data-testid={`post-edit-panel-${p.id}`}
                    className="mt-4 border-t border-[#EFEFEF] pt-5 grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-2"
                  >
                    <div className="md:col-span-2">
                      <label className="overline block mb-2">Description</label>
                      <textarea
                        data-testid="edit-description"
                        rows={3}
                        value={editForm.caption}
                        onChange={(e) => setEditForm((f) => ({ ...f, caption: e.target.value }))}
                        className="editorial-input resize-none"
                      />
                    </div>
                    <div>
                      <label className="overline block mb-2">Artist</label>
                      <select
                        data-testid="edit-artist"
                        value={editForm.artist}
                        onChange={(e) => setEditForm((f) => ({ ...f, artist: e.target.value }))}
                        className="editorial-input cursor-pointer"
                      >
                        {ARTISTS.filter((a) => a.role !== "Management").map((a) => (
                          <option key={a.id} value={a.id}>{a.name}</option>
                        ))}
                      </select>
                      <label className="overline block mt-6 mb-2">Tags <span className="normal-case tracking-normal text-[#999]">(comma separated)</span></label>
                      <input
                        data-testid="edit-tags"
                        value={editForm.tags}
                        onChange={(e) => setEditForm((f) => ({ ...f, tags: e.target.value }))}
                        className="editorial-input"
                      />
                    </div>
                    <div className="md:col-span-3 mt-4">
                      <div className="overline mb-3">Alt Text <span className="normal-case tracking-normal text-[#999]">(per image)</span></div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {p.images.map((im, i) => (
                          <div key={i}>
                            <div className="aspect-square bg-[#F5F5F5] overflow-hidden">
                              <img src={im.data_url} alt="" className="w-full h-full object-cover" style={{ filter: "grayscale(100%)" }} />
                            </div>
                            <input
                              data-testid={`edit-image-alt-${i}`}
                              value={editForm.alts[i] || ""}
                              onChange={(e) =>
                                setEditForm((f) => ({
                                  ...f,
                                  alts: f.alts.map((a, idx) => (idx === i ? e.target.value : a)),
                                }))
                              }
                              placeholder="e.g. Fine line rose tattoo on the forearm"
                              className="editorial-input text-sm mt-2"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="md:col-span-3 flex justify-end gap-4 mt-5">
                      <button
                        type="button"
                        onClick={cancelEdit}
                        data-testid="edit-cancel"
                        className="text-xs tracking-[0.22em] uppercase px-6 py-3 border border-[#0A0A0A]/30 text-[#0A0A0A] hover:border-[#0A0A0A] transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => saveEdit(p.id)}
                        disabled={saving}
                        data-testid="edit-save"
                        className="inline-flex items-center gap-3 bg-[#0A0A0A] text-white px-8 py-3 text-xs tracking-[0.28em] uppercase font-medium hover:bg-[#333] transition-colors disabled:opacity-60"
                      >
                        {saving ? "Saving…" : "Save Changes"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
